import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Heart,
  Eye,
  Shield,
  FileCheck,
  Award,
  FileText,
  Calendar,
  UserCheck,
  GraduationCap,
  HeartHandshake,
  Activity,
  Smile,
  Clock,
  Sparkles,
  Cloud,
  X,
} from "lucide-react";

import FadeIn from "../ui/FadeIn";
import AnimatedCounter from "../ui/AnimatedCounter";
import keylogo from "../../images/keylogo.png";

// Import the PDF Documents & Images
import annualReportPdf from "../../docs/KU Annual Report - 2025-26.pdf";
import annualReportPreviousPdf from "../../docs/KU Annual Report - 2024-25.pdf";
import darpanPdf from "../../docs/Darpan KINDNESS UNLIMITED REGISTRATION FORM.pdf";
import survivalImg from "../../images/SurvivalKindest.jpg";
import survivalPdf from "../../docs/Survival-of-the-Kindest-Article.pdf";
import researchPaper from "../../docs/Executive Summary KU Research.pdf";
import studyImage from "../../images/study.png";
import wkmCertificate from "../../images/wkm-certificate.jpg";

const stats = [
  { label: "Givers x 2", value: 220000, icon: Users, color: "from-pink-300 to-rose-400", desc: "Dedicated volunteers sharing their time and care" },
  { label: "Receivers x 2", value: 5600000, icon: Heart, color: "from-cyan-300 to-blue-400", desc: "Beneficiaries touched by kindness across India" },
  { label: "Observers", value: 8200000, icon: Eye, color: "from-emerald-300 to-teal-400", desc: "Lives impacted by witnessing acts of kindness" },
  { label: "Years of Impact", value: 21, icon: Calendar, color: "from-purple-300 to-fuchsia-400", desc: "Over two decades of continuous service and dedication" },
  { label: "Volunteers Engaged", value: 40000, icon: UserCheck, color: "from-amber-300 to-orange-400", desc: "Individuals actively participating in our mission" },
  { label: "Schools & Colleges Reached", value: 120, icon: GraduationCap, color: "from-sky-300 to-indigo-400", desc: "Educational institutions participating in our programs" },
  { label: "NGO & Community Partners", value: 80, icon: HeartHandshake, color: "from-lime-300 to-green-400", desc: "Community organizations collaborating with us" },
  { label: "Volunteering Interventions", value: 500, icon: Activity, color: "from-fuchsia-300 to-pink-400", desc: "Successful volunteering campaigns and events completed" },
  { label: "Lives Positively Impacted", value: 100000, icon: Smile, color: "from-yellow-300 to-amber-400", desc: "Individuals who have directly benefited from our initiatives" },
  { label: "Volunteer Hours", value: 125000, icon: Clock, color: "from-teal-300 to-cyan-400", desc: "Total time dedicated by our passionate volunteers" },
  { label: "Acts of Kindness", value: 1000000, icon: Sparkles, color: "from-rose-300 to-red-400", desc: "Total acts of kindness facilitated across the community" }
];

const trustItems = [
  { icon: Shield, title: "Trust Registration", detail: "Trust Registration No. - E22702 (Registered on July 01, 2005)" },
  { icon: FileCheck, title: "12-A Registration", detail: "12-A Registration No. - TR/39936 (Registered on April 18, 2006)" },
  { icon: Award, title: "80G Tax Exemption", detail: "80 G Exemption applicable of the IT Act - your donations are tax-deductible" },
];

const allocations = [
  { label: "Programmes & Engagements", percent: 75 },
  { label: "Operations & Outreach", percent: 15 },
  { label: "Administration", percent: 10 },
];

const sdgGoals = [
  { src: "https://sdgs.un.org/sites/default/files/goals/goal-1.png", alt: "SDG 1: No Poverty", label: "No Poverty" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-2.png", alt: "SDG 2: Zero Hunger", label: "Zero Hunger" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-3.png", alt: "SDG 3: Good Health and Well-being", label: "Good Health" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-4.png", alt: "SDG 4: Quality Education", label: "Quality Education" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-5.png", alt: "SDG 5: Gender Equality", label: "Gender Equality" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-6.png", alt: "SDG 6: Clean Water and Sanitation", label: "Clean Water" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-7.png", alt: "SDG 7: Affordable and Clean Energy", label: "Clean Energy" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-8.png", alt: "SDG 8: Decent Work and Economic Growth", label: "Economic Growth" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-9.png", alt: "SDG 9: Industry, Innovation and Infrastructure", label: "Innovation" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-10.png", alt: "SDG 10: Reduced Inequalities", label: "Reduced Inequalities" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-11.png", alt: "SDG 11: Sustainable Cities and Communities", label: "Sustainable Cities" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-12.png", alt: "SDG 12: Responsible Consumption and Production", label: "Responsible Consumption" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-13.png", alt: "SDG 13: Climate Action", label: "Climate Action" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-14.png", alt: "SDG 14: Life Below Water", label: "Life Below Water" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-15.png", alt: "SDG 15: Life on Land", label: "Life on Land" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-16.png", alt: "SDG 16: Peace, Justice and Strong Institutions", label: "Peace & Justice" },
  { src: "http://sdgs.un.org/sites/default/files/goals/goal-17.png", alt: "SDG 17: Partnerships for the Goals", label: "Partnerships" },
];

// Unique header content for each section
const headerData = {
  0: {
    eyebrow: "Our Impact",
    title: "Kindness in Numbers",
    desc: "Every act of giving creates a ripple multiplying across givers, receivers, and observers across India.",
  },
  1: {
    eyebrow: "Transparency & Trust",
    title: "Your kindness is in safe hands",
    desc: "Kindness Unlimited is a registered trust committed to accountability, compliance, and measurable impact.",
  },
  2: {
    eyebrow: "Global Goals",
    title: "Our Commitment to Global Goals",
    desc: "All 17 Sustainable Development Goals guide our journey towards a world where all individuals can thrive.",
  },
  3: {
    eyebrow: "Year in Review",
    title: "Annual Report",
    desc: "Explore a comprehensive look into our progress, programs, and the collective impact we made this year.",
  },
  4: {
    eyebrow: "Research & Publications",
    title: "Research Papers",
    desc: "Exploring the science, sociology, and profound impact of kindness and altruism.",
  }
};

const ImpactHub = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const location = useLocation();

  // Sync section with URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    if (tab === "transparency") setActiveTab(1);
    else if (tab === "sdg") setActiveTab(2);
    else if (tab === "report") setActiveTab(3);
    else if (tab === "research") setActiveTab(4);
    else setActiveTab(0); // numbers or default
  }, [location.search]);

  return (
    <div className="min-h-screen bg-ku-cream">
      {/* Unique Dynamic Hero Header */}
      <section className="relative py-16 md:py-20 bg-ku-gradient overflow-hidden">
        <div className="absolute inset-0 bg-noise opacity-20" aria-hidden />
        <div className="ku-container relative text-center">
          <FadeIn key={activeTab}>
            <span className="ku-eyebrow text-ku-lavender">
              {headerData[activeTab].eyebrow}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white mt-3 mb-4 leading-tight">
              {headerData[activeTab].title}
            </h1>
            <p className="font-body text-white/80 text-lg max-w-2xl mx-auto text-justify sm:text-center">
              {headerData[activeTab].desc}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="py-12 md:py-16">

        {/* Impact Numbers */}
        {activeTab === 0 && (
          <div className="ku-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6 mb-12">
              {stats.map((stat, i) => (
                <FadeIn key={stat.label} delay={i * 0.05}>
                  <motion.div whileHover={{ y: -8 }} className="ku-glass rounded-3xl p-8 text-center cursor-default h-full flex flex-col">
                    {/* Icon Container with Pastel Gradient */}
                    <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${stat.color} mb-4 shadow-card mx-auto`}>
                      <stat.icon className="w-8 h-8 text-white" />
                    </div>

                    <p className="font-accent text-[10px] md:text-xs font-bold text-ku-violet uppercase tracking-widest mb-1 mt-2">
                      {stat.label}
                    </p>

                    <div className="font-display text-3xl md:text-4xl font-bold text-ku-royal flex items-center justify-center mb-2">
                      <AnimatedCounter value={stat.value} />
                    </div>

                    <p className="font-body text-gray-600 text-xs mt-auto leading-relaxed text-center">
                      {stat.desc}
                    </p>
                  </motion.div>
                </FadeIn>
              ))}
            </div>

            {/* Quote */}
            <FadeIn>
              <blockquote className="max-w-3xl mx-auto text-center ku-glass rounded-2xl p-6 md:p-8 border-t-4 border-ku-royal">
                <p className="font-display text-xl md:text-2xl font-bold text-ku-royal italic leading-snug">
                  &ldquo;Today&apos;s Youth will be compassionate leaders of tomorrow&rdquo;
                </p>
                <p className="font-accent text-ku-violet mt-3 text-xs md:text-sm">
                  — Kindness Unlimited Vision
                </p>
              </blockquote>
            </FadeIn>
          </div>
        )}

        {/* Transparency */}
        {activeTab === 1 && (
          <div className="ku-container">
            {/* GuideStar Certification */}
            <FadeIn>
              <div className="ku-glass rounded-3xl p-6 md:p-8 mb-10 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-14 max-w-5xl mx-auto">
                <div className="relative flex-shrink-0 my-2">
                  <div className="w-56 h-56 md:w-64 md:h-64 rounded-full border-[4px] border-dashed border-ku-gold/50 animate-spin-slow absolute inset-0 m-auto" style={{ width: '112%', height: '112%', top: '-6%', left: '-6%' }} />
                  <div className="relative w-56 h-56 md:w-64 md:h-64 rounded-full border-[5px] border-ku-gold bg-white p-2 shadow-gold-elevated animate-award-glow flex items-center justify-center">
                    <img
                      src={keylogo}
                      alt="GuideStar India Certified — GSN 12619"
                      className="w-full h-full object-contain rounded-full"
                    />
                  </div>
                </div>
                <div className="text-center md:text-left flex-1 max-w-2xl">
                  <span className="inline-block px-5 py-1.5 rounded-full bg-gradient-to-r from-ku-gold to-ku-gold-light text-ku-purple-deep font-display font-extrabold text-lg tracking-wide shadow-gold-glow mb-3">
                    GSN 12619
                  </span>
                  <h3 className="font-display text-3xl md:text-4xl font-bold text-ku-royal mb-2">GuideStar India Certified</h3>
                  <p className="font-body text-gray-600 text-base leading-relaxed mb-4">
                    Certified by GuideStar India, affirming our commitment to transparency,
                    accountability, and good governance in the non-profit sector.
                  </p>
                  <a
                    href="https://guidestarindia.org/GSN-12619/KindnessUnlimited"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-ku-violet text-white font-accent font-semibold text-sm hover:bg-ku-purple shadow-card hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                  >
                    Verify on GuideStar India →
                  </a>
                </div>
              </div>
            </FadeIn>

{/* World Kindness Movement (WKM) National Peak Body Certificate */}
            <FadeIn delay={0.1}>
              <div className="ku-glass rounded-3xl p-6 sm:p-8 lg:p-10 mb-10 max-w-5xl mx-auto border-t-4 border-ku-violet shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  {/* Left: Certificate Preview */}
                  <div className="lg:col-span-5 flex justify-center lg:justify-start">
                    <div
                      onClick={() => setShowCertificateModal(true)}
                      className="relative w-full max-w-sm rounded-2xl overflow-hidden border border-ku-soft bg-white p-2.5 shadow-card cursor-pointer group transition-transform duration-300 hover:-translate-y-1"
                    >
                      <img
                        src={wkmCertificate}
                        alt="World Kindness Movement - Certificate of The National Peak Body"
                        className="w-full aspect-[4/3] object-contain rounded-xl bg-[#faf8f5] transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                      <div className="absolute inset-0 bg-ku-purple/40 backdrop-blur-[1.5px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-center justify-center text-white font-accent text-sm font-semibold gap-2">
                        <Eye className="w-5 h-5" /> Click to Expand
                      </div>
                    </div>
                  </div>

                  {/* Right: Text & Actions */}
                  <div className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left">
                    <span className="inline-block px-4 py-1 rounded-full bg-ku-soft text-ku-violet font-accent text-xs font-bold tracking-wider uppercase mb-3">
                      Global Recognition
                    </span>

                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-ku-royal mb-3">
                      World Kindness Movement (WKM)
                    </h3>

                    <p className="font-body text-gray-600 text-sm sm:text-base leading-relaxed mb-6 max-w-xl text-justify">
                      Kindness Unlimited is honoured to be a member of The World Kindness Movement since 2005, receiving international endorsement as India&apos;s Peak National Kindness Body.
                      <br />
                      Together with over 24 member nations, we work to inspire individuals, communities, and institutions toward greater goodwill, understanding, and a kinder world.
                    </p>

                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                      <button
                        type="button"
                        onClick={() => setShowCertificateModal(true)}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-ku-violet text-white font-accent font-semibold text-sm hover:bg-ku-purple shadow-card hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                      >
                        <Eye className="w-4 h-4" />
                        View Certificate
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Fund Allocation, Darpan & Trust Registrations */}
            <div className="grid lg:grid-cols-2 gap-10 items-start mb-10">
              <div className="space-y-6">
                <FadeIn>
                  <div className="ku-glass rounded-3xl p-8">
                    <h3 className="font-display text-2xl font-bold text-ku-royal mb-6">Fund Allocation Overview</h3>
                    <div className="space-y-6">
                      {allocations.map((item) => (
                        <div key={item.label}>
                          <div className="flex justify-between text-sm font-accent mb-2">
                            <span className="text-gray-700 font-medium">{item.label}</span>
                            <span className="font-bold text-ku-violet">{item.percent}%</span>
                          </div>
                          <div className="h-3 rounded-full bg-ku-soft overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              whileInView={{ width: `${item.percent}%` }}
                              transition={{ duration: 1.2, ease: "easeOut" }}
                              viewport={{ once: true }}
                              className="h-full rounded-full bg-ku-gradient"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </FadeIn>

                <FadeIn delay={0.15}>
                  <div className="ku-glass rounded-3xl p-8 border-t-4 border-emerald-500">
                    <div className="flex flex-col sm:flex-row items-center gap-5">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center flex-shrink-0 shadow-card">
                        <FileCheck className="w-7 h-7 text-white" />
                      </div>
                      <div className="text-center sm:text-left flex-1">
                        <h4 className="font-display text-lg font-bold text-ku-royal mb-1">NGO Darpan Registered</h4>
                        <p className="text-sm text-gray-600 font-body leading-relaxed mb-3">
                          Registered on the NITI Aayog NGO Darpan portal, affirming our commitment to transparency.
                        </p>
                        <a
                          href={darpanPdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 text-white font-accent font-semibold text-xs shadow-card hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                        >
                          <FileCheck className="w-3.5 h-3.5" />
                          View Darpan Certificate
                        </a>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              </div>

              <div className="grid sm:grid-cols-1 gap-4">
                {trustItems.map((item, i) => (
                  <FadeIn key={item.title} delay={i * 0.08}>
                    <div className="ku-card p-6 h-full">
                      <div className="w-10 h-10 rounded-xl bg-ku-soft flex items-center justify-center mb-4">
                        <item.icon className="w-5 h-5 text-ku-violet" />
                      </div>
                      <h4 className="font-display font-bold text-ku-royal mb-2">{item.title}</h4>
                      <p className="font-body text-sm text-gray-600 leading-relaxed text-justify">{item.detail}</p>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SDG Goals */}
        {activeTab === 2 && (
          <div className="ku-container">
            <div className="grid lg:grid-cols-2 gap-12 mb-12">
              <FadeIn direction="left">
                <div className="space-y-5 font-body text-gray-700 leading-relaxed text-justify">
                  <p>All socio-economic progress is deeply rooted in our collective empathy and compassion for humanity. All of the global sustainable development goals serve as the compass for our journey.</p>
                  <p>From eradicating poverty and ensuring quality education to tackling climate change and promoting peace, each of the 17 SDGs reflects our shared commitment to fostering human flourishing.</p>
                  <p>Kindness Unlimited&apos;s work with youth contributes to these universal goals, fostering a more compassionate world where human flourishing is at the heart of every action.</p>
                </div>
              </FadeIn>
              <FadeIn direction="right" delay={0.1}>
                <blockquote className="ku-glass rounded-3xl p-8 border-l-4 border-ku-royal h-full flex items-center">
                  <p className="font-display text-2xl md:text-3xl font-semibold text-ku-royal italic leading-snug">
                    &ldquo;Today&apos;s Youth will be compassionate leaders of tomorrow&rdquo;
                  </p>
                </blockquote>
              </FadeIn>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
              {sdgGoals.map((goal, i) => (
                <FadeIn key={goal.label} delay={i * 0.04}>
                  <div className="ku-card p-3 text-center group hover:scale-105 transition-transform">
                    <img src={goal.src} alt={goal.alt} loading="lazy" className="w-full aspect-square object-contain mb-2" />
                    <span className="text-xs font-accent font-medium text-ku-violet line-clamp-2">{goal.label}</span>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        )}

        {/* Annual Report */}
        {activeTab === 3 && (
          <div className="ku-container py-8">
            <div className="max-w-4xl mx-auto space-y-6">
              {[
                { title: "KU Annual Report 2025-26", href: annualReportPdf },
                { title: "KU Annual Report 2024-25", href: annualReportPreviousPdf },
              ].map((report) => (
                <FadeIn key={report.title}>
                  <div className="bg-white rounded-[2rem] p-8 md:p-12 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 shadow-sm border border-ku-soft/50 hover:shadow-lg transition-all duration-300">
                    <div className="w-32 h-32 md:w-36 md:h-36 shrink-0 bg-[#f5f0ff] rounded-2xl flex items-center justify-center text-ku-violet">
                      <FileText className="w-16 h-16" strokeWidth={1.5} />
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <h3 className="font-display text-2xl md:text-3xl font-bold text-ku-royal mb-6">
                        {report.title}
                      </h3>
                      <div className="flex flex-wrap items-center justify-center gap-4">
                        <a
                          href={report.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-8 py-3 bg-ku-violet text-white font-medium rounded-full hover:shadow-lg transition-all hover:-translate-y-1"
                        >
                          <Eye className="w-5 h-5" />
                          View Online
                        </a>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        )}

        {/* Research Papers Section */}
        {activeTab === 4 && (
          <div className="ku-container py-8 space-y-8">

            {/* FIRST PAPER: Survival of the Kindest */}
            <FadeIn delay={0.1}>
              <div className="bg-white rounded-[2rem] p-6 md:p-10 max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 shadow-sm border border-ku-soft/50 hover:shadow-lg transition-all duration-300">

                {/* Left: Booklet Cover Thumbnail */}
                <div className="w-full md:w-5/12 lg:w-1/3 h-64 md:h-72 rounded-2xl overflow-hidden bg-ku-soft/30 shrink-0 relative flex items-center justify-center border border-gray-100 p-4">
                  <img
                    src={survivalImg}
                    alt="Survival of the Kindest Cover"
                    className="absolute inset-0 w-full h-full object-contain p-2"
                  />
                </div>

                {/* Right: Content & Action */}
                <div className="w-full md:w-7/12 lg:w-2/3 flex flex-col items-center md:items-start text-center md:text-left">
                  <h3 className="font-display text-2xl font-bold text-ku-royal mb-3">
                    Survival of the Kindest
                  </h3>
                  <p className="font-body text-gray-600 text-sm leading-relaxed mb-6 text-justify">
                    The booklet by Gentletude Altruism in India. Discover profound insights into how kindness, compassion, and altruistic behavior actively shape our society and individual well-being.
                  </p>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                    <a
                      href={survivalPdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-ku-violet text-white font-medium rounded-full hover:shadow-lg transition-all hover:-translate-y-1 text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      View PDF
                    </a>
                    <a
                      href="https://share.google/ujOTxbk9lQ9Sjwe5y"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-ku-soft text-ku-purple font-medium rounded-full hover:bg-ku-soft/80 transition-all hover:-translate-y-1 text-sm border border-gray-200"
                    >
                      <Cloud className="w-4 h-4 text-ku-purple" />
                      Google Drive
                    </a>
                  </div>
                </div>

              </div>
            </FadeIn>


            {/* SECOND PAPER: TISS Research Study */}
            <FadeIn>
              <div className="bg-white rounded-[2rem] p-6 md:p-10 max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 shadow-sm border border-ku-soft/50 hover:shadow-lg transition-all duration-300">

                {/* Left: Study Image Thumbnail */}
                <div className="w-full md:w-5/12 lg:w-1/3 h-64 md:h-72 rounded-2xl overflow-hidden bg-ku-soft/30 shrink-0 relative flex items-center justify-center border border-gray-100 p-4">
                  <img
                    src={studyImage}
                    alt="TISS Research Study Findings"
                    className="absolute inset-0 w-full h-full object-contain p-2"
                  />
                </div>

                {/* Right: Content & Action */}
                <div className="w-full md:w-7/12 lg:w-2/3 flex flex-col items-center md:items-start text-center md:text-left">
                  <h3 className="font-display text-2xl font-bold text-ku-royal mb-3">
                    Find Yourself Through Volunteering
                  </h3>
                  <p className="font-body text-gray-600 text-sm leading-relaxed mb-6 text-justify">
                    A TISS-led year-long research study on Volunteering and its Impact on Mental Health in College Students. Structured volunteering serves as a proven, proactive model for enhancing youth mental well-being and holistic growth.
                  </p>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                    <a
                      href={researchPaper}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-3 bg-ku-violet text-white font-medium rounded-full hover:shadow-lg transition-all hover:-translate-y-1 text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      View Report
                    </a>
                  </div>
                </div>

              </div>
            </FadeIn>

          </div>
        )}

      </div>

      {/* Certificate Modal View */}
      <AnimatePresence>
        {showCertificateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCertificateModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="relative z-10 bg-white rounded-3xl p-4 sm:p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col items-center"
            >
              <div className="w-full flex items-center justify-between pb-3 mb-3 border-b border-ku-soft">
                <h4 className="font-display text-lg font-bold text-ku-royal">
                  World Kindness Movement - Certificate of The National Peak Body
                </h4>
                <button
                  type="button"
                  onClick={() => setShowCertificateModal(false)}
                  className="p-1 rounded-full text-gray-500 hover:text-ku-violet hover:bg-ku-soft transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <img
                src={wkmCertificate}
                alt="World Kindness Movement Certificate"
                className="w-full h-auto max-h-[70vh] object-contain rounded-xl border border-ku-soft"
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImpactHub;